/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from 'react';
import { dataGrid } from '@harmony/enablers/react';
import { Dictionary } from '../../Models/Dictionary';
import { getHarmonyScope } from '../../../Services/Services.Secondary';
import { defaultComparison, getRowsForDataGrid } from '../../../Utilities/Helpers/Harmony.Helper';

const DataGrid = getHarmonyScope().forReact(dataGrid);

export type HarmonyTableRow = {
  id: number;
  cells: Dictionary<string>;
  selected?: boolean;
  disabled?: boolean;
  customData?: any;
};

export type HarmonyTableColumn = {
  field: string;
  content: string;
  sortable?: boolean;
  display?: {
    hideAt?: number;
    width?: string;
    minWidth?: string;
    maxWidth?: string;
    lineClamp?: number;
    noWrap?: boolean;
  };
};

type TableProps = {
  id: string;
  loading: boolean;
  columns: HarmonyTableColumn[];
  rows?: HarmonyTableRow[];
  rowsData?: string[][];
  htmlRowsContent?: JSX.Element[] | null;
  noRowsMessage: string;
  sortFunction?: (rowA: HarmonyTableRow, rowB: HarmonyTableRow, event: any) => number; //Function to compare two rows when sorting
  handleSortFunction?: (event: any, rows: HarmonyTableRow[]) => void; //Function to handle all sorting logic for the table
  navigation?: JSX.Element | null;
  controls?: JSX.Element | null;
  sortBy?: string;
  select?: 'multiple' | 'single' | undefined;
  selectFunction?: (e: any) => any;
  fixedHeading?: boolean;
  styles?: JSX.Element[] | null;
};

export default class Table extends React.Component<TableProps, any> {
  constructor(props: TableProps) {
    super(props);
    this.handleSort = this.handleSort.bind(this);
  }

  private defaultHandleSort = (event: any, rows: HarmonyTableRow[]) => {
    const sortFunc = !this.props.sortFunction ? defaultComparison : this.props.sortFunction;
    rows.sort((a, b) => {
      return sortFunc(a, b, event);
    });
  };

  private handleSort = this.props.handleSortFunction ?? this.defaultHandleSort;

  private handleSelect(event: any) {
    if (this.props.selectFunction) {
      this.props.selectFunction(event);
    }
  }

  public render() {
    const rows = this.props.rows ? this.props.rows : getRowsForDataGrid(this.props.columns, this.props.rowsData ?? []);
    const showRows = rows && !this.props.loading;

    return (
      <DataGrid
        id={this.props.id}
        loading={this.props.loading}
        rows={rows}
        columns={this.props.columns}
        onHeSort={(e: any) => this.handleSort(e, rows)}
        select={this.props.select}
        onHeRowSelectChange={(e: any) => this.handleSelect(e)}
        fixedHeading={this.props.fixedHeading ?? false}
        sortBy={this.props.sortBy}
        sortAscending={true}
      >
        {this.props.controls && (
          <span slot="data-grid-controls" style={{ display: 'flex', width: '100%' }}>
            {this.props.controls}
          </span>
        )}
        {showRows && this.props.htmlRowsContent}
        {this.props.styles}
        {!this.props.loading && <span slot="no-records">{this.props.noRowsMessage}</span>}
        {this.props.navigation ? this.props.navigation : <></>}
        
      </DataGrid>
    );
  }
}